import { action, computed, makeObservable, observable } from 'mobx';
import { FamilyMember, StatisticsValues } from 'types/censusService';

export interface CensusReviewDomainParams {
  groupId: string;
  enrollmentPeriodId: string;
}

const INITIAL_STATISTICS: StatisticsValues = {
  total: 0,
  employees: 0,
  dependents: 0,
  errors: 0,
};

export default abstract class ICensusReviewPageStore<DomainParams extends CensusReviewDomainParams> {
  protected abstract _domainData: DomainParams;
  protected _statistics: StatisticsValues = INITIAL_STATISTICS;
  protected _families: FamilyMember[][] = [];
  protected _headers: string[] = [];
  protected _isLoading: boolean = true;
  protected _errors: string[] = [];
  protected _incompatibilityErrorMsg: string | null = null;

  protected _setIncompatibilityErrorMsg(msg: string): void {
    this._incompatibilityErrorMsg = msg;
  }

  get isLoading(): boolean {
    return this._isLoading;
  }

  get statistics(): StatisticsValues {
    return this._statistics;
  }

  get hasErrors(): boolean {
    return this._statistics.errors > 0;
  }

  get families(): FamilyMember[][] {
    return this._families;
  }

  get hasFamiliesWithErrors(): boolean {
    return this._families.reduce((acc, family) => acc || family.some(({ invalid }) => invalid), false);
  }

  get headers(): string[] {
    return this._headers;
  }

  get incompatibilityErrorMsg(): string | null {
    return this._incompatibilityErrorMsg;
  }

  public setIsLoading(isLoading: boolean): void {
    this._isLoading = isLoading;
  }

  abstract onBackRedirect(): void;
  abstract onCompleteRedirect(): void;
  abstract getStatistics(): Promise<void>;
  abstract onCensusSubmit(): Promise<void>;

  constructor() {
    makeObservable<
      ICensusReviewPageStore<DomainParams>,
      | '_statistics'
      | '_isLoading'
      | '_families'
      | '_headers'
      | '_errors'
      | '_incompatibilityErrorMsg'
      | '_setIncompatibilityErrorMsg'
    >(this, {
      _statistics: observable,
      _families: observable,
      _errors: observable,
      _headers: observable,
      _isLoading: observable,
      _incompatibilityErrorMsg: observable,
      _setIncompatibilityErrorMsg: action.bound,
      incompatibilityErrorMsg: computed,
      hasErrors: computed,
      hasFamiliesWithErrors: computed,
      families: computed,
      headers: computed,
      isLoading: computed,
      statistics: computed,
      setIsLoading: action.bound,
    });
  }
}
